// pages/ProjectsPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Folder,
  FolderOpen,
  Trash2,
  Edit2,
  Music2,
  Clock,
  Layers,
  Loader,
  Search,
  Mic2,
  Zap,
  ChevronRight,
  SortAsc,
  SortDesc,
  Grid3X3,
  List,
} from "lucide-react";
import api from "../../services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
};

// ── Rename inline input ───────────────────────────────────────────────────────
const RenameInput = ({ value, onSave, onCancel }) => {
  const [name, setName] = useState(value);
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = () => {
    if (name.trim()) onSave(name.trim());
    else onCancel();
  };
  return (
    <input
      ref={ref}
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onCancel();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      className="w-full rounded-lg px-2.5 py-1 text-sm text-white focus:outline-none"
      style={{
        background: "rgba(139,92,246,0.12)",
        border: "1px solid rgba(139,92,246,0.4)",
      }}
    />
  );
};

// ── AVATAR_GRADIENTS mirrors the studio components ────────────────────────────
const AVATAR_GRADIENTS = [
  "from-violet-500 via-purple-500 to-fuchsia-500",
  "from-cyan-500 via-blue-500 to-indigo-500",
  "from-emerald-500 via-teal-500 to-cyan-500",
  "from-rose-500 via-pink-500 to-fuchsia-500",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-lime-500 via-green-500 to-emerald-500",
];

// ── Project Card (grid view) ──────────────────────────────────────────────────
const ProjectCard = ({ project, index, onOpen, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const blockCount = project.blocks?.length ?? project.blockCount ?? 0;
  const dur = formatDuration(project.duration);
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await onDelete(project.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={() => !renaming && onOpen(project.id)}
      className="group relative flex flex-col rounded-2xl cursor-pointer transition-all duration-200 overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(139,92,246,0.3)";
        e.currentTarget.style.background = "rgba(139,92,246,0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
      }}
    >
      {/* Colour banner */}
      <div
        className={`h-24 bg-gradient-to-br ${gradient} relative flex items-center justify-center`}
      >
        <div
          className="absolute inset-0"
          style={{ background: "rgba(0,0,0,0.25)" }}
        />
        <Folder size={36} className="text-white/60 relative z-10" />
        {/* Actions */}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(true);
            }}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-white transition-colors"
            style={{
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Edit2 size={11} />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-red-400 disabled:opacity-40 transition-colors"
            style={{
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(8px)",
            }}
          >
            {deleting ? (
              <Loader size={11} className="animate-spin" />
            ) : (
              <Trash2 size={11} />
            )}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        {renaming ? (
          <RenameInput
            value={project.name}
            onSave={(name) => {
              onRename(project.id, name);
              setRenaming(false);
            }}
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <p className="text-sm font-semibold text-white truncate">
            {project.name}
          </p>
        )}

        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {blockCount > 0 && (
            <span className="text-[10px] text-gray-600 flex items-center gap-1">
              <Layers size={9} /> {blockCount} block
              {blockCount !== 1 ? "s" : ""}
            </span>
          )}
          {dur && (
            <span className="text-[10px] text-gray-600 flex items-center gap-1">
              <Clock size={9} /> {dur}
            </span>
          )}
          {project.backgroundMusic && (
            <span className="text-[10px] text-gray-600 flex items-center gap-1">
              <Music2 size={9} /> music
            </span>
          )}
        </div>
        <p className="text-[10px] text-gray-700 mt-2">
          {formatDate(project.updatedAt || project.createdAt)}
        </p>
      </div>

      {/* Open arrow */}
      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight size={14} className="text-violet-400" />
      </div>
    </div>
  );
};

// ── Project Row (list view) ───────────────────────────────────────────────────
const ProjectRow = ({ project, index, onOpen, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const blockCount = project.blocks?.length ?? project.blockCount ?? 0;
  const dur = formatDuration(project.duration);
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await onDelete(project.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={() => !renaming && onOpen(project.id)}
      className="group flex items-center gap-4 px-4 py-3.5 rounded-xl cursor-pointer transition-all duration-150"
      style={{ border: "1px solid transparent" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.03)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = "transparent";
      }}
    >
      {/* Folder icon */}
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${gradient}`}
      >
        <Folder size={16} className="text-white/70" />
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        {renaming ? (
          <RenameInput
            value={project.name}
            onSave={(name) => {
              onRename(project.id, name);
              setRenaming(false);
            }}
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <p className="text-sm font-semibold text-white truncate">
            {project.name}
          </p>
        )}
      </div>

      {/* Meta */}
      <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
        <span className="text-[11px] text-gray-600 w-16 text-right font-mono">
          {blockCount} block{blockCount !== 1 ? "s" : ""}
        </span>
        {dur && (
          <span className="text-[11px] text-gray-600 w-12 text-right font-mono">
            {dur}
          </span>
        )}
        <span className="text-[11px] text-gray-700 w-20 text-right">
          {formatDate(project.updatedAt || project.createdAt)}
        </span>
      </div>

      {/* Actions */}
      {!renaming && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(true);
            }}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-300 transition-colors"
          >
            <Edit2 size={11} />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-600 hover:text-red-400 disabled:opacity-40 transition-colors"
          >
            {deleting ? (
              <Loader size={11} className="animate-spin" />
            ) : (
              <Trash2 size={11} />
            )}
          </button>
        </div>
      )}

      <ChevronRight
        size={14}
        className="text-gray-700 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getVoiceProjects();
      setProjects(res.projects || []);
    } catch (e) {
      console.error("Failed to load projects:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.createVoiceProject({ name: "Untitled Project" });
      const project = res.project;
      setProjects((prev) => [project, ...prev]);
      // Navigate into studio with this project
      localStorage.setItem("currentProjectId", project.id);
      navigate("/studio");
    } catch (e) {
      console.error("Failed to create project:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleOpen = (id) => {
    localStorage.setItem("currentProjectId", id);
    navigate("/studio");
  };

  const handleRename = async (id, name) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
    try {
      await api.updateVoiceProject(id, { name });
    } catch {
      load();
    }
  };

  const handleDelete = async (id) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
    try {
      await api.deleteVoiceProject(id);
    } catch {
      load();
    }
  };

  const filtered = projects
    .filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return sortDesc ? tb - ta : ta - tb;
    });

  return (
    <div
      className="flex flex-col h-full min-h-screen text-white"
      style={{ background: "#070a10" }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-8 py-5 flex-shrink-0"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(7,10,16,0.9)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">
            All Projects
          </h1>
          <p className="text-xs text-gray-600 mt-0.5">
            {loading
              ? "Loading…"
              : `${projects.length} project${projects.length !== 1 ? "s" : ""}`}
          </p>
        </div>

        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all"
          style={{
            background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
            boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
          }}
        >
          {creating ? (
            <>
              <Loader size={13} className="animate-spin" /> Creating…
            </>
          ) : (
            <>
              <Plus size={14} /> New Project
            </>
          )}
        </button>
      </div>

      {/* ── Toolbar ── */}
      <div
        className="flex items-center gap-3 px-8 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
      >
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
            size={13}
          />
          <input
            type="text"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl text-sm text-white placeholder-gray-700 focus:outline-none transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = "rgba(139,92,246,0.4)")
            }
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.07)")
            }
          />
        </div>

        <div className="flex items-center gap-1 ml-auto">
          {/* Sort */}
          <button
            onClick={() => setSortDesc((d) => !d)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-gray-500 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            {sortDesc ? <SortDesc size={12} /> : <SortAsc size={12} />}
            {sortDesc ? "Newest" : "Oldest"}
          </button>

          {/* View toggle */}
          <div
            className="flex rounded-xl overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.07)" }}
          >
            {[
              { mode: "grid", Icon: Grid3X3 },
              { mode: "list", Icon: List },
            ].map(({ mode, Icon }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className="w-8 h-8 flex items-center justify-center transition-all"
                style={{
                  background:
                    viewMode === mode
                      ? "rgba(139,92,246,0.2)"
                      : "rgba(255,255,255,0.03)",
                  color: viewMode === mode ? "#a78bfa" : "#4b5563",
                }}
              >
                <Icon size={13} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div
        className="flex-1 overflow-y-auto px-8 py-6"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(139,92,246,0.2) transparent",
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64 gap-3">
            <div
              className="w-6 h-6 rounded-full animate-spin"
              style={{
                border: "2px solid rgba(139,92,246,0.2)",
                borderTopColor: "#7c3aed",
              }}
            />
            <span className="text-sm text-gray-600">Loading projects…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: "rgba(139,92,246,0.08)",
                border: "1px solid rgba(139,92,246,0.12)",
              }}
            >
              <Mic2 size={26} className="text-violet-700" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">
                {search ? "No projects match your search" : "No projects yet"}
              </p>
              <p className="text-xs text-gray-700 mt-0.5">
                {search
                  ? "Try a different name"
                  : "Create your first project to get started"}
              </p>
            </div>
            {!search && (
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white mt-2 disabled:opacity-60 transition-all"
                style={{
                  background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                  boxShadow: "0 4px 16px rgba(124,58,237,0.2)",
                }}
              >
                <Plus size={14} /> Create Project
              </button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {/* New project card */}
            <div
              onClick={handleCreate}
              className="flex flex-col items-center justify-center rounded-2xl cursor-pointer transition-all duration-200 min-h-[160px]"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed rgba(255,255,255,0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)";
                e.currentTarget.style.background = "rgba(139,92,246,0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                e.currentTarget.style.background = "rgba(255,255,255,0.02)";
              }}
            >
              {creating ? (
                <Loader size={20} className="text-violet-600 animate-spin" />
              ) : (
                <>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      background: "rgba(139,92,246,0.12)",
                      border: "1px solid rgba(139,92,246,0.2)",
                    }}
                  >
                    <Plus size={18} className="text-violet-500" />
                  </div>
                  <p className="text-xs font-semibold text-gray-600">
                    New Project
                  </p>
                </>
              )}
            </div>

            {filtered.map((p, i) => (
              <ProjectCard
                key={p.id}
                project={p}
                index={i}
                onOpen={handleOpen}
                onRename={handleRename}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* List header */}
            <div
              className="flex items-center gap-4 px-4 pb-2 mb-1"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
            >
              <div className="w-10 flex-shrink-0" />
              <p className="flex-1 text-[10px] font-bold text-gray-700 uppercase tracking-widest">
                Name
              </p>
              <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-widest w-16 text-right">
                  Blocks
                </span>
                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-widest w-12 text-right">
                  Duration
                </span>
                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-widest w-20 text-right">
                  Updated
                </span>
              </div>
              <div className="w-20 flex-shrink-0" />
            </div>
            <div className="space-y-1">
              {filtered.map((p, i) => (
                <ProjectRow
                  key={p.id}
                  project={p}
                  index={i}
                  onOpen={handleOpen}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Stats bar ── */}
      {!loading && projects.length > 0 && (
        <div
          className="flex items-center gap-6 px-8 py-3 flex-shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          {[
            { label: "Total Projects", value: projects.length },
            {
              label: "Total Blocks",
              value: projects.reduce(
                (acc, p) => acc + (p.blocks?.length ?? p.blockCount ?? 0),
                0,
              ),
            },
            {
              label: "With Music",
              value: projects.filter((p) => p.backgroundMusic).length,
            },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <span className="text-sm font-bold text-violet-400">
                {stat.value}
              </span>
              <span className="text-[11px] text-gray-700">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
